// src/pages/mission/PostulerModal.tsx
import React, { useState, useRef } from "react";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    Button,
    Box,
    Text,
    Flex,
    useToast,
    Alert,
    AlertIcon,
    CloseButton,
    useColorModeValue
} from "@chakra-ui/react";
import { FiUpload, FiFile } from "react-icons/fi";

interface PostulerModalProps {
    isOpen: boolean;
    onClose: () => void;
    missionId: string;
    onPostuler: (missionId: string, file: File) => Promise<boolean>;
}

const PostulerModal: React.FC<PostulerModalProps> = ({
                                                         isOpen,
                                                         onClose,
                                                         missionId,
                                                         onPostuler
                                                     }) => {
    const [file, setFile] = useState<File | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const toast = useToast();
    const dropBg = useColorModeValue('gray.50', 'gray.700');
    const accentColor = "#420073";

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            setError(null);
            setFile(selectedFile);
        }
    };

    const handleSubmit = async () => {
        if (!file) {
            setError("Veuillez sélectionner un fichier PDF");
            return;
        }

        setIsSubmitting(true);
        try {
            const success = await onPostuler(missionId, file);
            if (success) {
                onClose();
                setFile(null);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : "Erreur inconnue");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader color={accentColor}>Postuler à la mission</ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                    {error && (
                        <Alert status="error" mb={4} borderRadius="md">
                            <AlertIcon />
                            {error}
                            <CloseButton
                                position="absolute"
                                right="8px"
                                top="8px"
                                onClick={() => setError(null)}
                            />
                        </Alert>
                    )}

                    <Text mb={4}>Veuillez uploader votre CV (format PDF uniquement)</Text>

                    <Box
                        borderWidth={2}
                        borderStyle="dashed"
                        borderRadius="lg"
                        p={10}
                        textAlign="center"
                        cursor="pointer"
                        onClick={() => fileInputRef.current?.click()}
                        bg={dropBg}
                        mb={4}
                    >
                        <input
                            type="file"
                            ref={fileInputRef}
                            hidden
                            accept="application/pdf"
                            onChange={handleFileChange}
                        />
                        <Flex direction="column" align="center">
                            <FiUpload size={24} color={accentColor} />
                            <Text mt={2}>Glissez-déposez ou cliquez pour sélectionner</Text>
                            <Text fontSize="sm" color="gray.500">PDF uniquement (max 50Mo)</Text>
                        </Flex>
                    </Box>

                    {file && (
                        <Flex align="center" justify="space-between" bg="purple.50" p={3} borderRadius="md">
                            <Flex align="center">
                                <FiFile size={20} color={accentColor} />
                                <Text ml={2}>{file.name}</Text>
                            </Flex>
                            <Text fontSize="sm" color="gray.500">
                                {(file.size / 1024 / 1024).toFixed(2)} Mo
                            </Text>
                        </Flex>
                    )}

                    <Button
                        colorScheme="purple"
                        mt={4}
                        onClick={handleSubmit}
                        isLoading={isSubmitting}
                        bg={accentColor}
                        isDisabled={!file}
                    >
                        Envoyer ma candidature
                    </Button>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default PostulerModal;